import React, { useEffect, useState } from "react";
import { getSettings } from "../lib/api";
import { toast } from "sonner";
import { Phone, Mail, Info, HeartPulse, Flame, Shield, Users, EyeOff, ShieldAlert, Languages, Sun, Moon } from "lucide-react";
import { useSilentMode } from "../lib/useSilentMode";
import { usePanicEnabled } from "../lib/usePanicEnabled";
import { useLang, useT } from "../lib/lang";
import { useTheme } from "../lib/theme";
import { vibrate } from "../lib/silent";

const TYPE_ORDER = ["Medical", "Fire", "Police", "FAMA"];
const TYPE_ICONS = { Medical: HeartPulse, Fire: Flame, Police: Shield, FAMA: Users };
const DEFAULTS = {
  Medical: { phone: "15", email: "" },
  Fire: { phone: "18", email: "" },
  Police: { phone: "17", email: "" },
  FAMA: { phone: "144", email: "" },
};

export default function SettingsPage() {
  const [dispatchers, setDispatchers] = useState({});
  const [silent, setSilent] = useSilentMode();
  const [panicOn, setPanicOn] = usePanicEnabled();
  const [curLang, setLang] = useLang();
  const [curTheme, setTheme] = useTheme();
  const [t, labels] = useT();

  useEffect(() => {
    (async () => {
      try {
        const s = await getSettings();
        setDispatchers(s.dispatchers || DEFAULTS);
      } catch (_e) {
        setDispatchers(DEFAULTS);
      }
    })();
  }, []);

  return (
    <div className="flex-1 flex flex-col" data-testid="settings-page">
      <header className="px-5 pt-5 pb-3">
        <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-rescue-mute">{t.settingsKicker}</div>
        <h1 className="font-display text-3xl font-black tracking-tight leading-none mt-1">{t.settingsTitle}</h1>
        <p className="text-sm text-rescue-mute mt-2">{t.settingsIntro}</p>
      </header>

      {/* LANGUAGE TOGGLE */}
      <div className="mx-5 mb-4 border-2 border-rescue-line bg-white" data-testid="language-card">
        <div className="flex items-center gap-3 px-4 pt-4 pb-2">
          <div className="shrink-0 w-10 h-10 flex items-center justify-center border-2 border-rescue-line text-rescue-ink">
            <Languages className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div className="text-xs font-bold uppercase tracking-[0.2em]">Langue · Language</div>
        </div>
        <div className="grid grid-cols-2 gap-1.5 p-3 pt-1">
          <button
            type="button"
            data-testid="lang-fr"
            onClick={() => { setLang("fr"); vibrate(40); }}
            className={`py-2.5 text-[11px] font-bold uppercase tracking-[0.12em] transition-colors ${
              curLang === "fr" ? "bg-rescue-ink text-white" : "border-2 border-rescue-line text-rescue-ink hover:border-rescue-ink"
            }`}
          >
            Français
          </button>
          <button
            type="button"
            data-testid="lang-en"
            onClick={() => { setLang("en"); vibrate(40); }}
            className={`py-2.5 text-[11px] font-bold uppercase tracking-[0.12em] transition-colors ${
              curLang === "en" ? "bg-rescue-ink text-white" : "border-2 border-rescue-line text-rescue-ink hover:border-rescue-ink"
            }`}
          >
            English
          </button>
        </div>
      </div>

      {/* THEME TOGGLE — strictly Light or Dark. No "Auto / System" option
          on purpose: Samsung Internet's force-dark heuristic was overriding
          the app's own choice, so the app theme is decoupled from the
          phone's system theme. See Iter53g. */}
      <div className="mx-5 mb-4 border-2 border-rescue-line bg-white" data-testid="theme-card">
        <div className="flex items-center gap-3 px-4 pt-4 pb-2">
          <div className="shrink-0 w-10 h-10 flex items-center justify-center border-2 border-rescue-line text-rescue-ink">
            {curTheme === "dark"
              ? <Moon className="w-5 h-5" strokeWidth={2.5} />
              : <Sun className="w-5 h-5" strokeWidth={2.5} />}
          </div>
          <div className="text-xs font-bold uppercase tracking-[0.2em]">{t.themeLabel || "Thème"}</div>
        </div>
        <div className="grid grid-cols-2 gap-1.5 p-3 pt-1">
          <button
            type="button"
            data-testid="theme-light"
            onClick={() => { setTheme("light"); vibrate(40); }}
            aria-pressed={curTheme === "light"}
            className={`py-2.5 text-[11px] font-bold uppercase tracking-[0.12em] transition-colors flex items-center justify-center gap-1.5 ${
              curTheme === "light" ? "bg-rescue-ink text-white" : "border-2 border-rescue-line text-rescue-ink hover:border-rescue-ink"
            }`}
          >
            <Sun className="w-3.5 h-3.5" /> {t.themeLight || "Clair"}
          </button>
          <button
            type="button"
            data-testid="theme-dark"
            onClick={() => { setTheme("dark"); vibrate(40); }}
            aria-pressed={curTheme === "dark"}
            className={`py-2.5 text-[11px] font-bold uppercase tracking-[0.12em] transition-colors flex items-center justify-center gap-1.5 ${
              curTheme === "dark" ? "bg-rescue-ink text-white" : "border-2 border-rescue-line text-rescue-ink hover:border-rescue-ink"
            }`}
          >
            <Moon className="w-3.5 h-3.5" /> {t.themeDark || "Sombre"}
          </button>
        </div>
      </div>

      {/* SILENT MODE TOGGLE */}
      <div className="mx-5 mb-4 border-2 border-rescue-ink bg-white" data-testid="silent-mode-card">
        <button
          type="button"
          onClick={() => {
            const next = !silent;
            setSilent(next);
            vibrate(next ? 120 : 60);
            toast.success(next ? `${t.silentTitle} : ${t.silentOn}` : `${t.silentTitle} : ${t.silentOff}`);
          }}
          data-testid="silent-mode-toggle"
          className="w-full flex items-start gap-3 p-4 text-left hover:bg-rescue-bg transition-colors"
        >
          <div className={`shrink-0 w-10 h-10 flex items-center justify-center border-2 ${silent ? "border-rescue-ink bg-rescue-ink text-white" : "border-rescue-line text-rescue-ink"}`}>
            <EyeOff className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs font-bold uppercase tracking-[0.2em]">{t.silentTitle}</div>
              <span
                className={`text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-0.5 ${silent ? "bg-rescue-ink text-white" : "bg-rescue-line text-rescue-mute"}`}
                data-testid="silent-mode-state"
              >
                {silent ? t.silentOn : t.silentOff}
              </span>
            </div>
            <div className="text-[11px] text-rescue-red font-bold mt-1">{t.silentSubtitle}</div>
            <p className="text-[11px] text-rescue-mute leading-relaxed mt-2">{t.silentDesc}</p>
          </div>
        </button>
      </div>

      {/* PANIC GESTURE TOGGLE */}
      <div className="mx-5 mb-4 border-2 border-rescue-ink bg-white" data-testid="panic-mode-card">
        <button
          type="button"
          onClick={() => {
            const next = !panicOn;
            setPanicOn(next);
            vibrate(next ? 120 : 60);
            toast.success(next ? "Geste de panique : Activé" : "Geste de panique : Désactivé");
          }}
          data-testid="panic-mode-toggle"
          className="w-full flex items-start gap-3 p-4 text-left hover:bg-rescue-bg transition-colors"
        >
          <div className={`shrink-0 w-10 h-10 flex items-center justify-center border-2 ${panicOn ? "border-mali-yellow bg-mali-yellow text-rescue-ink" : "border-rescue-line text-rescue-ink"}`}>
            <ShieldAlert className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs font-bold uppercase tracking-[0.2em]">Geste de panique</div>
              <span
                className={`text-[10px] font-bold uppercase tracking-[0.2em] px-2 py-0.5 ${panicOn ? "bg-mali-yellow text-rescue-ink" : "bg-rescue-line text-rescue-mute"}`}
                data-testid="panic-mode-state"
              >
                {panicOn ? t.silentOn : t.silentOff}
              </span>
            </div>
            <div className="text-[11px] text-rescue-red font-bold mt-1">5 tapes rapides · Anti-violence</div>
            <p className="text-[11px] text-rescue-mute leading-relaxed mt-2">
              Tapotez l&apos;écran <span className="font-bold text-rescue-ink">5 fois en moins de 2 s</span> pour déclencher une <span className="font-bold text-rescue-ink">alerte Police silencieuse</span>. Une fenêtre de 3 secondes permet d&apos;annuler en cas de fausse manipulation. Limite : 1 alerte / 5 minutes.
            </p>
          </div>
        </button>
      </div>

      <div className="mx-5 mt-2 mb-3 flex items-center justify-between">
        <span className="bg-rescue-ink text-white px-2.5 py-1 text-[10px] font-bold tracking-[0.2em] uppercase" data-testid="defaults-badge">
          {t.defaultsBadge}
        </span>
      </div>

      {/* Dispatcher numbers — READ-ONLY (security fix, Sep 2026: editing
          national dispatch routing is now admin-only, see Système >
          Paramètres in the command center; this page no longer PUTs). */}
      <div className="mx-5 space-y-3" data-testid="dispatchers-readonly">
        {TYPE_ORDER.map((type) => {
          const Icon = TYPE_ICONS[type];
          const entry = dispatchers[type] || DEFAULTS[type];
          const label = labels[type]?.label || type;
          return (
            <div key={type} className="border-2 border-rescue-line bg-white p-4" data-testid={`dispatcher-card-${type.toLowerCase()}`}>
              <div className="flex items-center gap-2 mb-2">
                <Icon className="w-5 h-5 text-rescue-red" strokeWidth={2.5} />
                <div className="text-xs font-bold uppercase tracking-[0.2em]">{t.dispatcherFor}{label}</div>
              </div>
              <div className="flex items-center gap-4 text-sm font-mono text-rescue-ink">
                <span className="flex items-center gap-1.5" data-testid={`dispatcher-phone-${type.toLowerCase()}`}>
                  <Phone className="w-3.5 h-3.5 text-rescue-mute" /> {entry.phone || DEFAULTS[type].phone}
                </span>
                {entry.email && (
                  <span className="flex items-center gap-1.5 truncate" data-testid={`dispatcher-email-${type.toLowerCase()}`}>
                    <Mail className="w-3.5 h-3.5 text-rescue-mute" /> {entry.email}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mx-5 mt-4 mb-8 border-2 border-rescue-line bg-white p-4 flex gap-3">
        <Info className="w-5 h-5 shrink-0 text-rescue-red mt-0.5" />
        <div className="text-xs text-rescue-mute leading-relaxed">{t.apiInfo}</div>
      </div>
    </div>
  );
}
